import type { InjectionKey } from 'vue';
import { JsonSchema, Layout } from '@jsonforms/core';
import { STEP_KEYS, WIDGET_TYPES } from 'src/constants';
import { Session, Source } from './api';
import { Address, Phone } from './overlays';
import { RecursivePartial } from './helpers';

export type StepKey = (typeof STEP_KEYS)[keyof typeof STEP_KEYS];
export enum StepType {
  REFERRAL = 'referral',
  CLAIMS = 'claims',
  ASSISTANCE = 'assistance',
}

export type WidgetType = keyof typeof WIDGET_TYPES;

export type ContextSelector = {
  title: string;
  options: SelectorOptions[];
  hidden?: boolean;
  tips?: {
    title: string;
    bullets: string[];
  };
};

export type SelectorOptions = {
  title: string;
  description: string;
  icon: string;
  value: string;
  key: AvailableBrands | AvailableCollisionTypes;
};
export interface Theme {
  primary?: string;
  secondary?: string;
  positive?: string;
  negative?: string;
  fontPrimary?: string;
  fontSecondary?: string;
  background?: string;
  backgroundSoft?: string;
  accordion?: string;
  banner?: string;
  icon?: string;
  borderRadius?: string;
  shadow?: string;
  primaryBoldUrl?: string;
  primaryRegularUrl?: string;
  secondaryBoldUrl?: string;
  secondaryRegularUrl?: string;
  components?: {
    input?: string;
    inputLabel?: string;
    justifyNavbar?: string;
    checkbox?: string;
    radioGroup?: string;
    button?: {
      primary?: string;
      secondary?: string;
      yesNo?: string;
      ghost?: string;
      step?: string;
      back?: string;
    };
    card?: string;
    contactInfoCard?: string;
    accordion?: string;
    banner?: string;
    title?: string;
    body?: string;
  };
}

export interface ContactConfig {
  phone: string;
  email: string;
  link?: string;
}

export interface PublicUserConfig {
  id?: string;
  firstName?: string;
  lastName?: string;
  drivingLicenseNumber?: string;
  address?: Address;
  phone?: string | Phone;
  email?: string;
  retailerName?: string;
}

export interface VehicleConfig {
  licensePlate?: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: string;
  color?: string;
  fuelType?: string;
  engineSize?: number;
}

export interface Policy {
  /**
   * The name of the policy provider.
   */
  provider?: string;
  /**
   * The policy number it is mandatory for claim submission but can be field later.
   */
  number?: string;
  /**
   * The date when the policy becomes valid it is a key value to determine if the policy is active.
   */
  validFrom?: string;
  /**
   * The date when the policy expires it is a key value to determine if the policy is active.
   */
  validTo?: string;
  /**
   * The holder of the policy this is the main contact information will be used for claim submission.
   */
  holder?: PublicUserConfig;
  /**
   * The vehicle associated with the policy.
   */
  vehicle?: VehicleConfig;
  /**
   * The list of other contacts linked to the policy, will be used if the policy holder wasn't the driver.
   */
  linkedContacts?: PublicUserConfig[];
}

export interface InsuranceConfig {
  provider?: string;
  number?: string;
}

export interface MissingProperties {
  selectedUser?: PublicUserConfig;
  selectedInsurance?: InsuranceConfig;
  vehicle?: VehicleConfig;
  isModified?: Array<'user' | 'insurance' | 'vehicle'>;
}

export interface ContactProperties {
  user?: PublicUserConfig;
}

export type WidgetConfig = {
  contact: ContactConfig;
  initialStepKey?: StepKey;
  theme?: Theme;
  reference?: string;
  fontFamily?: string;
  fontFamilySecondary?: string;
  privacyPolicyUrl?: string;
  policyHubUrl?: string;
  state?: RecursivePartial<Source> & Pick<Source, 'stepKey'>;
  analytics?: RecursivePartial<CookieConfig>;
} & WidgetUserDetails;

export type WidgetUserDetails =
  | {
      policy: Policy | Policy[];
      vehicle?: undefined;
      driver?: undefined;
    }
  | {
      vehicle?: VehicleConfig;
      driver?: PublicUserConfig;
      policy?: undefined;
    }
  | {
      vehicle?: undefined;
      driver?: undefined;
      policy?: undefined;
    };

export interface SessionTrackingOn {
  sessionTracking: true;
  currentUserId: string;
}

export interface SessionTrackingOff {
  sessionTracking?: false;
  currentUserId?: string;
}

export type SessionTracking = SessionTrackingOn | SessionTrackingOff;

export type ClientConfig =
  | 'audi'
  | 'bmw'
  | 'toyota'
  | 'mopar'
  | 'jeep'
  | 'default'
  | 'jaguar'
  | 'jlr'
  | 'landrover'
  | 'mercedes'
  | 'mini'
  | 'referral'
  | 'volkswagen'
  | 'bentley'
  | 'vwg'
  | 'volvo'
  | 'tesla'
  | 'aa'
  | 'suzuki'
  | 'cinch'
  | 'crashdaddy'
  | 'samsung'
  | 'common'
  | null
  | undefined;

export const AVAILABLE_BRANDS = [
  'jaguar',
  'landrover',
  'volkswagen',
  'audi',
  'skoda',
  'seat',
  'cupra',
  'volvo',
  'tesla',
  'suzuki',
  'mercedes',
  'common',
] as const;
export const AVAILABLE_REFERRALS = ['eCall', 'sos', 'bCall', 'rsa', 'mrc', 'aCall'] as const;
export type AvailableBrands = (typeof AVAILABLE_BRANDS)[number];
export type AvailableCollisionTypes = (typeof AVAILABLE_REFERRALS)[number];

export interface FormOptions {
  submit?: boolean;
  reportAnother?: string | { title?: string; label: string };
  actionLabel?: string;
}

export interface StepForm {
  schema: JsonSchema;
  uischema: Layout;
  options: FormOptions;
}

export interface StepComponent {
  form?: StepForm;
  name: string;
  description: string;
  icon: string;
  child?: StepsConfig;
  key: StepKey;
  confirmStep?: StepForm;
  recoveryNotRequiredInHoursConfirmStep?: StepForm;
  recoveryNotRequiredOutOfHoursConfirmStep?: StepForm;
  recoveryIsRequiredInHoursConfirmStep?: StepForm;
  recoveryIsRequiredOutOfHoursConfirmStep?: StepForm;
}

export interface StepsConfig {
  title: string;
  description: string;
  components: StepComponent[];
  welcomeStep?: StepForm;
  contextStep?: Record<string, ContextSelector>;
  contactInfo?: ContactInfoCardConfig;
  type?: StepType;
}

export type ContactInfoCardConfig = {
  title?: string | undefined;
  description?: string;
  icon?: string;
  phone?: PhoneConfig | PhoneConfig[];
  mail?: MailConfig | MailConfig[];
};

export interface ApiWidgetOptions {
  apiKey: string;
  authToken?: string;
  steps: StepsConfig;
  isConfigLoading: boolean;
  error: string;
  name: string;
  allowedDomains: string[];
  correspondenceEmail: string[];
  rootKey: InjectionKey<string>;
  id: string;
  selectedClaim?: StepKey;
  clientTheme?: ClientConfig;
  iconTheme?: ClientConfig;
  callbackUrl?: string;
  learnMoreUrl?: string;
  callbackOnSubmit?: boolean;
  privacyPolicyUrl?: string;
  context?: Record<string, string>;
  integrations?: Array<{
    name: string;
    settings?: {
      businessHours?: Array<{
        open?: string;
        close?: string;
      }>;
      timezone?: string;
    };
  }>;
  settings?: {
    repairBrands?: RepairBrand[];
    businessHours?: Array<{
      open?: string;
      close?: string;
    }>;
    timezone?: string;
    type?: StepType;
    mainTitle?: string;
    contactInformation: Partial<Record<AvailableBrands | 'default', Array<PhoneConfig | MailConfig>>>;
    outOfHoursPhone?: string;
    cookie?: CookieConfig;
  };
  insurance?: Omit<Policy, 'holder' | 'vehicle'>;
  /**
   * The reference of the submission you are trying to load
   */
  reference?: string;
  /**
   * The session id of the session you are trying to load
   */
  session?: string;
}

export interface RepairBrand {
  id: number;
  name: string;
  axicloud_id: string;
  managed_by_panel: number;
  managed_by_certification: number;
  created_at: string;
  updated_at: string;
}

export type PhoneConfig = {
  phone?: string;
  number?: string;
  description?: string;
  title?: string;
};
export type MailConfig = {
  description?: string;
  mail?: string;
  address?: string;
  title?: string;
};

export type PublicWidgetOptions = {
  clientId: string;
  /**
   * The reference of the submission you are trying to load
   */
  reference?: string;
  /**
   * The session id of the session you are trying to load
   */
  session?: string;
  /**
   * This function is called when a submission is successful.
   * @param data - The data that was submitted.
   * @returns void
   */
  onSuccess?: (data: Source) => void;
  /**
   * This function is called when a submission is unsuccessful.
   * @param error - The error that was received.
   * @returns void
   */
  onError?: (error: unknown) => void;
  /**
   * This function is called when any changes happens in widget.
   * @param data - The data that is available currently.
   * @returns void
   */
  onChange?: (data: Source) => void;
} & WidgetConfig &
  SessionTracking;

export interface UserConfig extends PublicUserConfig {
  new?: boolean;
  selected?: boolean;
}

export type GlobalConfig = PublicWidgetOptions &
  ApiWidgetOptions & {
    sessionData: Session;
  };

export type ImageSection = {
  key: string;
  previewUrl: string;
};

export interface CookieConfig {
  /**
   * Master switch for GA/Clarity/W3W cookies
   */
  enabled?: boolean;
  /**
   * Overrides default GA ID
   */
  gaId?: string;
  /**
   * Overrides default Clarity ID
   */
  clarityId?: string;
  /**
   * Overrides default OneTrust domain ID
   */
  oneTrustDomainId?: string;
  /**
   * Overrides default OpenReplay project key
   */
  openReplayProjectKey?: string;
}
